import { TIME_DISPLAY_POLICY, type TimeKind, type TimeZoneSource } from '@/config/timeDisplayPolicy';
import { useCenterTimeZone } from '@/hooks/useCenterTimeZone';
import { useAuthStore } from '@/stores/authStore';
import { dateLocaleForCountry, SHORT_DATE_OPTIONS } from '@/utils/dateLocale';
import { formatDayOnly, parseDayOnly } from '@/utils/formatDayOnly';
import { formatInCenterTime, getTimeZoneAbbreviation, middayInTimeZone, nowInCenterTime, toCenterTime } from '@/utils/timezone';
import { useCallback, useMemo } from 'react';

type DateInput = Date | string | number;

const getDeviceTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const resolveTimeZone = (source: TimeZoneSource, centerTimeZone: string): string => {
  if (source === 'center') return centerTimeZone;
  if (source === 'utc') return 'UTC';

  // A written day carries no offset to apply, so it is read in the device zone and stays on the
  // day the API wrote — see parseDayOnly.
  return getDeviceTimeZone();
};

/**
 * The zone whose abbreviation labels the rendered time. It is not always the zone the value is read
 * in: an attendance time is read as UTC precisely because that UTC wall clock is the center's, so
 * the label a guardian needs to see is the center's.
 */
const resolveLabelTimeZone = (source: TimeZoneSource, centerTimeZone: string): string | undefined => {
  if (source === 'none') return undefined;

  return source === 'device' ? getDeviceTimeZone() : centerTimeZone;
};

const readValue = (value: DateInput, source: TimeZoneSource, timeZone: string): Date | undefined => {
  if (source === 'none') return parseDayOnly(String(value));

  const zonedValue = toCenterTime(value, timeZone);

  return Number.isNaN(zonedValue.getTime()) ? undefined : zonedValue;
};

/**
 * The clock a given kind of value is rendered on. Components name what they are showing — a session
 * time, a cancellation deadline, a report day — and TIME_DISPLAY_POLICY decides the rest.
 */
export const useTimeFor = (kind: TimeKind) => {
  const centerTimeZone = useCenterTimeZone();
  const { userData } = useAuthStore();
  const { zone, zoneLabel } = TIME_DISPLAY_POLICY[kind];

  const dateLocale = dateLocaleForCountry(userData?.center?.countryCode);

  const timeZone = resolveTimeZone(zone, centerTimeZone);
  const labelTimeZone = resolveLabelTimeZone(zone, centerTimeZone);

  const zoned = useCallback((value: DateInput) => readValue(value, zone, timeZone) ?? new Date(Number.NaN), [zone, timeZone]);

  const now = useCallback(() => nowInCenterTime(timeZone), [timeZone]);

  // Renders nothing rather than throwing on a value the API left empty or malformed.
  const format = useCallback(
    (value: DateInput, pattern: string) => {
      const readable = readValue(value, zone, timeZone);

      return readable ? formatInCenterTime(readable, timeZone, pattern) : '';
    },
    [zone, timeZone],
  );

  /**
   * A value the API stamped `Z` without meaning it carries a wall clock, not a real instant, so the
   * abbreviation is taken at noon on that day — asking at the wall-clock time itself would name the
   * wrong side of a DST transition for a session within a few hours of one.
   */
  const zoneAbbreviation = useCallback(
    (value: DateInput) => {
      const readable = readValue(value, zone, timeZone);

      if (!labelTimeZone || !readable) return '';

      return getTimeZoneAbbreviation(zone === 'utc' ? middayInTimeZone(readable, labelTimeZone) : readable, labelTimeZone);
    },
    [labelTimeZone, zone, timeZone],
  );

  const formatWithZone = useCallback(
    (value: DateInput, pattern: string) => {
      const formatted = format(value, pattern);

      return formatted && zoneLabel ? `${formatted} ${zoneAbbreviation(value)}`.trim() : formatted;
    },
    [format, zoneAbbreviation, zoneLabel],
  );

  // Follows the center's country rather than the browser's locale, so a Canadian guardian on a
  // US-configured device reads DD/MM/YYYY here and on their invoices alike.
  const formatDate = useCallback(
    (value: DateInput, locale = dateLocale) => {
      if (zone === 'none') return formatDayOnly(String(value), locale);

      const parsed = new Date(value);

      return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString(locale, { ...SHORT_DATE_OPTIONS, timeZone });
    },
    [zone, timeZone, dateLocale],
  );

  return useMemo(
    () => ({ timeZone, zoned, now, format, formatWithZone, formatDate, zoneAbbreviation }),
    [timeZone, zoned, now, format, formatWithZone, formatDate, zoneAbbreviation],
  );
};
